import { get, toString } from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { StickyContainer, Sticky } from 'react-sticky';
import { grey10, grey25, navy, white } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import { Arrow, Close } from '@zocdoc/components/src/icons';
import { ThemedInsurancePicker as BaseInsurancePicker } from '@zocdoc/components/src/InsurancePicker';
import { ProcedureDropdown } from '@zocdoc/components/src/ProcedureDropdown';
import ScrollLock from '@zocdoc/components/src/ScrollLock';
import { FontFamily } from '@zocdoc/components/src/typography';

import LocationCardCarousel from '../../../Shared/LocationCardCarousel';
import UnbookableSection from '../UnbookableSection';
import {
    LOCALIZED_STRINGS,
    NUM_LOCATIONS_TO_SHOW_ON_PAGE_LOAD,
    PADDING_X_XXSMALL,
    PADDING_X_MEDIUM,
    PADDING_X_LARGE,
} from './constants';
import { BookingSubSection, BookingSubSectionWithNoLabelOnMobile, BookingSubSection1Z } from './BookingSubSection';
import BookingSectionNewExistingRadio from '../BookingSectionNewExistingRadio';
import BookingSectionTimesGrid from '../BookingSectionTimesGrid';
import addInsuranceModal from '../../../Shared/Modals/addInsuranceModal';
import * as TrackingConstants from '../../../EventTrackingConstants';
import { PROFILE_GA_CATEGORY, PROVIDER_STATUS } from '../../constants';
import { InviteDoctorForm } from '../InviteDoctorForm';
import { Button } from '@zocdoc/components/src/button';
import LocationAppointmentPicker from './LocationAppointmentPicker';
import { insuranceTypeFromProvider } from '../../../utils/insuranceTypeFrom';
import { sortLocationsByCurrentLocation } from './sortLocationsByCurrentLocation';
import getProfileLabelType from '../../utils/getProfileLabelType';

const InsurancePicker = addInsuranceModal(BaseInsurancePicker);

export class BookingSectionView extends PureComponent {
    static propTypes = {
        ab: PropTypes.object,
        bookingSpotlightIsOn: PropTypes.bool,
        carrierId: PropTypes.string,
        changeProcedure: PropTypes.func.isRequired,
        currentLocationCloudId: PropTypes.string,
        isDesktop: PropTypes.bool,
        isNewPatient: PropTypes.bool,
        planId: PropTypes.string,
        procedureId: PropTypes.string,
        provider: PropTypes.object,
        providerId: PropTypes.string.isRequired,
        showMap: PropTypes.func.isRequired,
        showNewExistingToggle: PropTypes.bool,
        theme: PropTypes.shape({
            profile: PropTypes.shape({
                notAvailableText: PropTypes.string,
                unbookableSection: PropTypes.shape({
                    markdown: PropTypes.string,
                }),
            }),
        }),
        trackClickExistingPatientType: PropTypes.func.isRequired,
        trackClickNewPatientType: PropTypes.func.isRequired,
        trackKeyDownExistingPatientType: PropTypes.func.isRequired,
        trackKeyDownNewPatientType: PropTypes.func.isRequired,
        trackProcedureDropdownChange: PropTypes.func.isRequired,
        trackProcedureDropdownClick: PropTypes.func.isRequired,
        turnBookingSpotlightOff: PropTypes.func.isRequired,
    };

    state = {
        showAllLocations: false,
    };

    turnBookingSpotlightOff = e => {
        if (e) e.stopPropagation();
        this.props.turnBookingSpotlightOff();
    };

    render() {
        const { isDesktop } = this.props;
        return (
            <Wrapper>
                <Sticky disableCompensation={!isDesktop}>
                    {stickyProps => this.renderBookingSection(calculateStickyStyle(stickyProps, isDesktop))}
                </Sticky>
            </Wrapper>
        );
    }

    renderBookingSection(style) {
        const { is1Z } = this.props;
        const headerText = is1Z ? LOCALIZED_STRINGS.BookingSectionHeader : LOCALIZED_STRINGS.BookingSectionButtonText;
        let Header;
        let Booking;

        if (is1Z) {
            Booking = 'div';
            Header = OneZSectionHeader;
        } else {
            Booking = WhiteLabelBooking;
            Header = WhiteLabelSectionHeader;
        }

        return (
            <Booking
                style={style}
            >
                <Header
                    data-test="booking-section-header"
                    onClick={this.props.trackClickBookingSectionHeader}
                    shouldShowNoAvailability={is1Z && this.shouldShowNoAvailability()}
                >
                    <FormattedMessage {...headerText} />
                    {!is1Z && (<UnderlineHeader />)}
                </Header>
                {this.props.provider.isPreview
                    ? this.renderInviteDoctorForm()
                    : this.renderInnerBookingSection()
                }
                {this.maybeRenderCloseButtonAndScrollLock()}
            </Booking>
        );
    }

    renderInviteDoctorForm() {
        const { is1Z, provider } = this.props;

        const inviteDoctorForm = (
            <InviteDoctorForm
                data-test="profile-invite-doctor-form"
                professionalName={provider.approvedFullName}
            />
        );

        return is1Z ? (
            <InnerBookingWrapper>
                {inviteDoctorForm}
            </InnerBookingWrapper>
        ) : inviteDoctorForm;
    }

    renderInnerBookingSection() {
        const { is1Z } = this.props;

        if (this.shouldShowNoAvailability()) {
            return this.renderNotAvailable();
        } else if (is1Z) {
            return (
                <InnerBookingWrapper>
                    {this.maybeRenderInsurancePicker()}
                    {this.maybeRenderReasonForYourVisit()}
                    {this.maybeRenderHaveYouSeenThisDoctorBefore1Z()}
                    {this.maybeRenderLocationAppointmentPickers()}
                </InnerBookingWrapper>
            );
        } else {
            return (
                <div>
                    {this.maybeRenderReasonForYourVisit()}
                    {this.maybeRenderHaveYouSeenThisDoctorBefore()}
                    {this.maybeRenderInsurancePicker()}
                    {this.maybeRenderOfficeLocations()}
                    {this.renderBookingControl()}
                </div>
            );
        }
    }

    renderNotAvailable() {
        const { is1Z, theme: { profile: { notAvailableText } } } = this.props;

        const notAvailableModule = [
            <NotAvailableText
                key="not-available-text"
                data-test="not-available-text"
            >
                {notAvailableText}
            </NotAvailableText>,
        ];

        if (is1Z) {
            return (
                <NotAvailableTextWrapper>
                    {notAvailableModule}
                </NotAvailableTextWrapper>
            );
        } else {
            notAvailableModule.push(
                <NotAvailableCloseButton
                    key="not-available-button"
                    onClick={this.turnBookingSpotlightOff}
                    style={Button.STYLE.SECONDARY}
                >
                    Close
                </NotAvailableCloseButton>
            );
            return notAvailableModule;
        }
    }

    maybeRenderReasonForYourVisit() {
        const {
            changeProcedure,
            directoryId,
            is1Z,
            isDesktop,
            procedureId,
            provider,
            providerId,
            showNewExistingToggle,
            trackProcedureDropdownChange,
            trackProcedureDropdownClick,
        } = this.props;

        if (!isDesktop && is1Z) {
            return;
        }

        const label = is1Z
            ? LOCALIZED_STRINGS.ProcedureDropdownLabel1Z
            : LOCALIZED_STRINGS.ProcedureDropdownLabel;
        const SubSectionContainer = is1Z ? BookingSubSection1Z : BookingSubSection;

        if (provider.procedures && provider.procedures.length) {
            return (
                <SubSectionContainer
                    data-test="booking-section-reason-for-your-visit"
                    label={label}
                    marginTop={showNewExistingToggle ? 20 : 25}
                >
                    <ProcedureDropdown
                        directoryId={directoryId}
                        onProcedureChange={changeProcedure}
                        providerId={providerId}
                        selected={procedureId}
                        trackDropdownClick={trackProcedureDropdownClick}
                        trackProcedureChange={trackProcedureDropdownChange}
                    />
                </SubSectionContainer>
            );
        }
    }

    maybeRenderHaveYouSeenThisDoctorBefore() {
        const {
            isNewPatient,
            provider,
            trackClickExistingPatientType,
            trackClickNewPatientType,
            trackKeyDownExistingPatientType,
            trackKeyDownNewPatientType,
        } = this.props;
        if (provider.isBookable) {
            const label = LOCALIZED_STRINGS.NewExistingRadioLabel;
            const labelValues = { name: provider.nameInSentence };
            return (
                <BookingSubSection
                    data-test="booking-section-have-you-seen-this-doctor-before"
                    label={label}
                    labelValues={labelValues}
                >
                    <BookingSectionNewExistingRadio
                        defaultValue={(isNewPatient === false) ? 'existing-patient' : 'new-patient'}
                        onClickExistingPatientType={trackClickExistingPatientType}
                        onClickNewPatientType={trackClickNewPatientType}
                        onKeyDownExistingPatientType={trackKeyDownExistingPatientType}
                        onKeyDownNewPatientType={trackKeyDownNewPatientType}
                    />
                </BookingSubSection>
            );
        }
        return null;
    }

    maybeRenderHaveYouSeenThisDoctorBefore1Z() {
        const {
            isDesktop,
            isNewPatient,
            provider,
            trackClickExistingPatientType,
            trackClickNewPatientType,
            trackKeyDownExistingPatientType,
            trackKeyDownNewPatientType,
            showNewExistingToggle,
        } = this.props;
        if (provider.isBookable && showNewExistingToggle) {
            const label = isDesktop ? LOCALIZED_STRINGS.NewExistingRadioLabelV2 :
                LOCALIZED_STRINGS.NewExistingRadioLabelV2Mobile;
            const labelValues = { profileType: getProfileLabelType(provider) };

            return (
                <BookingSubSection1Z
                    data-test="booking-section-have-you-seen-this-doctor-before"
                    hideLabelOnMobile={false}
                    inline={!isDesktop}
                    label={label}
                    labelValues={labelValues}
                    marginTop={20}
                >
                    <BookingSectionNewExistingRadio
                        defaultValue={(isNewPatient === false) ? 'existing-patient' : 'new-patient'}
                        onClickExistingPatientType={trackClickExistingPatientType}
                        onClickNewPatientType={trackClickNewPatientType}
                        onKeyDownExistingPatientType={trackKeyDownExistingPatientType}
                        onKeyDownNewPatientType={trackKeyDownNewPatientType}
                        shortText
                        small={!isDesktop}
                    />
                </BookingSubSection1Z>
            );
        }
    }

    maybeRenderInsurancePicker() {
        const {
            bookingSpotlightIsOn,
            carrierId,
            carrierName,
            clickBookAnyway,
            clickChangeInsurance,
            currentLocationCloudId,
            is1Z,
            isDesktop,
            planId,
            planName,
            provider,
            providerId,
            showNewExistingToggle,
        } = this.props;

        if (!isDesktop && is1Z) {
            return;
        }

        const SubSectionContainer = is1Z ? BookingSubSection1Z : BookingSubSection;
        const label = is1Z ? null : LOCALIZED_STRINGS.InsurancePickerLabel;
        const InsurancePickerWrapper = is1Z ? 'div' : InsurancePickerBorder;
        const insurancePickerAdditionalParams = {
            hideValidation: !is1Z,
            showValidationBackground: is1Z,
        };

        return (
            <SubSectionContainer
                data-test="booking-section-insurance-picker"
                label={label}
                marginTop={showNewExistingToggle ? 20 : 25}
            >
                <InsurancePickerWrapper>
                    <InsurancePicker
                        {...insurancePickerAdditionalParams}
                        bookingSpotlightIsOn={bookingSpotlightIsOn}
                        carrierId={carrierId}
                        carrierName={carrierName}
                        data-test="wrapped-insurance-picker"
                        gaCategory={PROFILE_GA_CATEGORY}
                        insuranceType={insuranceTypeFromProvider(provider)}
                        locationId={currentLocationCloudId}
                        onChange={this.handleInsuranceChange}
                        onClickBookAnyway={clickBookAnyway}
                        onClickChangeInsurance={clickChangeInsurance}
                        planId={planId}
                        planName={planName}
                        providerId={providerId}
                        providerName={provider.nameInSentence}
                        section={TrackingConstants.TARGETS.BOOK_AN_APPOINTMENT}
                    />
                </InsurancePickerWrapper>
            </SubSectionContainer>
        );
    }

    maybeRenderOfficeLocations() {
        const {
            carrierId,
            currentLocationCloudId,
            planId,
            provider,
            providerId,
            showMap,
        } = this.props;

        if (provider.locations && provider.locations.length) {
            return (
                <BookingSubSectionWithNoLabelOnMobile
                    data-test="booking-section-office-locations"
                    label={LOCALIZED_STRINGS.LocationCarouselLabel}
                >
                    <LocationCardCarousel
                        carrierId={carrierId}
                        locationId={currentLocationCloudId}
                        locations={provider.locations}
                        onClickExpandMap={showMap}
                        planId={planId}
                        providerId={providerId}
                        section={TrackingConstants.TARGETS.BOOK_AN_APPOINTMENT}
                    />
                </BookingSubSectionWithNoLabelOnMobile>
            );
        }
    }

    renderBookingControl() {
        const {
            carrierId,
            currentLocationCloudId,
            isDesktop,
            isNewPatient,
            nextAvailability,
            planId,
            procedureId,
            provider,
            providerId,
            theme,
        } = this.props;

        const isExistingPatient = isNewPatient === false;

        if (get(theme, 'profile.unbookableSection.markdown') && (!provider.isBookable || !nextAvailability)) {
            return (
                <UnbookableSection
                    insurance={{
                        carrierId,
                        planId,
                    }}
                    isNewPatient={isNewPatient}
                    provider={provider}
                />
            );
        } else {
            const relevantLocations = provider.locations.filter(
                loc => loc.id === currentLocationCloudId
            );
            const locationMonolithId = (relevantLocations && relevantLocations.length > 0) ?
                relevantLocations[0].monolithId : null;
            return (
                <BookingSectionTimesGrid
                    data-test="booking-section-timesgrid"
                    isExistingPatient={isExistingPatient}
                    isMobile={!isDesktop}
                    locationId={locationMonolithId}
                    procedureId={procedureId}
                    provider={provider}
                    providerId={providerId}
                />
            );
        }
    }

    maybeRenderLocationAppointmentPickers() {
        const {
            isDesktop,
            carrierId,
            currentLocationCloudId,
            is1Z,
            isNewPatient,
            nextAvailability,
            planId,
            procedureId,
            provider,
            providerId,
            showNewExistingToggle,
            theme,
        } = this.props;

        if (get(theme, 'profile.unbookableSection.markdown') && (!provider.isBookable || !nextAvailability)) {
            return (
                <UnbookableSection
                    insurance={{
                        carrierId,
                        planId,
                    }}
                    isNewPatient={isNewPatient}
                    provider={provider}
                />
            );
        } else if (!provider.locations || !provider.locations.length) {
            return;
        }

        const shouldShowAllLocations = !isDesktop || this.state.showAllLocations;

        const sortedLocations = sortLocationsByCurrentLocation(
            currentLocationCloudId,
            provider.locations
        );

        const locationsToRender = shouldShowAllLocations ?
            sortedLocations :
            sortedLocations.slice(0, NUM_LOCATIONS_TO_SHOW_ON_PAGE_LOAD);

        const shouldRenderSeeAllLocationsToggle = !shouldShowAllLocations &&
            provider.locations.length > NUM_LOCATIONS_TO_SHOW_ON_PAGE_LOAD;

        const SubSectionContainer = is1Z ? BookingSubSection1Z : BookingSubSectionWithNoLabelOnMobile;

        return (
            <SubSectionContainer
                data-test="booking-section-location-appointment-pickers"
                hideLabelOnMobile
                label={LOCALIZED_STRINGS.LocationAppointmentPickersLabel}
                marginTop={showNewExistingToggle ? 20 : 25}
            >
                {locationsToRender.map(location => (
                    <LocationAppointmentPicker
                        key={location.id}
                        carrierId={carrierId}
                        data-test="booking-section-timesgrid"
                        is1Z={is1Z}
                        isExistingPatient={isNewPatient === false}
                        isMobile={!isDesktop}
                        location={location}
                        locationId={location.id}
                        planId={planId}
                        procedureId={procedureId}
                        provider={provider}
                        providerId={providerId}
                    />
                ))}
                {shouldRenderSeeAllLocationsToggle && (
                    <SeeAllLocationsToggleWrapper>
                        <SeeAllLocationsToggle
                            data-test="booking-section-see-all-locations-toggle"
                            onClick={this.handleClickSeeAllLocations}
                        >
                            <SeeAllLocationsToggleIcon direction="down" />
                            <FormattedMessage
                                {...LOCALIZED_STRINGS.SeeAllLocations}
                                values={{ numberOfLocations: provider.locations.length }}
                            />
                        </SeeAllLocationsToggle>
                    </SeeAllLocationsToggleWrapper>
                )}
            </SubSectionContainer>
        );
    }

    maybeRenderCloseButtonAndScrollLock() {
        const {
            is1Z,
            isDesktop,
            bookingSpotlightIsOn,
        } = this.props;

        if (!isDesktop && !is1Z && bookingSpotlightIsOn) {
            return (
                <div>
                    {bookingSpotlightIsOn && !isDesktop && <ScrollLock />}
                    <CloseButton
                        data-test="booking-section-close-button"
                        onClick={this.turnBookingSpotlightOff}
                    />
                </div>
            );
        }
    }

    handleClickSeeAllLocations = () => {
        this.setState({ showAllLocations: true });
    };

    handleInsuranceChange = insurance => {
        const payload = {};

        if (insurance.carrier) {
            payload.carrierId = toString(insurance.carrier.id);

            if (insurance.plan) {
                payload.planId = toString(insurance.plan.id);
            }

            this.props.changeInsurance(payload);
        }
    };

    shouldShowNoAvailability = () => {
        const { provider, theme } = this.props;
        const { unbookableSection: { markdown } } = theme.profile;

        const isLockedOrChurnedOrNonBookable =
            provider.status === PROVIDER_STATUS.Locked ||
            provider.status === PROVIDER_STATUS.Churned ||
            !provider.isBookable;

        return !markdown && isLockedOrChurnedOrNonBookable && !provider.isPreview;
    }
}

export function calculateStickyStyle({ style, distanceFromTop, calculatedHeight }, isDesktop) {
    if (!isDesktop) {
        return {};
    }

    if (typeof window !== 'undefined' && calculatedHeight > window.innerHeight) {
        style = {};
        if (distanceFromTop <= 0) {
            style.position = 'relative';
            style.top = `-${calculatedHeight}px`;
        }
    }

    return style;
}

const InsurancePickerBorder = styled.div`
    border: 1px solid ${grey25};
`;

const Wrapper = styled(StickyContainer)`
    background-color: ${grey10};
    min-height: 100%;

    ${breakpoint.medium} {
        height: 100%;
    }
`;

const baseBookingStyles = css`
    ${breakpoint.medium} {
        padding: 22px ${PADDING_X_MEDIUM}px 52px;
    }

    ${breakpoint.large} {
        padding-left: ${PADDING_X_LARGE}px;
        padding-right: ${PADDING_X_LARGE}px;
    }
`;

const WhiteLabelBooking = styled.div`
    ${baseBookingStyles}
    -webkit-overflow-scrolling: touch;
    bottom: 0;
    left: 0;
    overflow: auto;
    padding: 20px ${PADDING_X_XXSMALL}px 52px;
    position: fixed;
    right: 0;
    top: 0;

    ${breakpoint.medium} {
        position: static;
        overflow: visible;
        top: auto;
        right: auto;
        left: auto;
        bottom: auto;
    }
`;

const WhiteLabelSectionHeader = styled.div`
    color: ${p => p.theme.profile.textColor};
    font-family: ${FontFamily.SharpSansBold};
    font-size: 20px;
    line-height: 1;
    padding-bottom: 8px;
    padding-top: 12px;
    text-align: center;

    ${breakpoint.medium} {
        font-size: 24px;
        padding-bottom: 4px;
        text-align: left;
    }
`;

const OneZSectionHeader = styled.div`
    background-color: ${p => p.theme.components.button.inverted.bgColor};
    color: ${p => p.theme.components.button.inverted.color};
    display: ${p => p.shouldShowNoAvailability ? 'none' : 'block'};
    font-family: ${FontFamily.SharpSansBold};
    font-size: 15px;
    letter-spacing: 0.1px;
    line-height: 20px;
    padding-bottom: 8px;
    padding-top: 8px;
    text-align: center;

    ${breakpoint.medium} {
        background-color: ${navy};
        color: ${white};
        display: block;
        font-size: 24px;
        line-height: 1;
        padding: 25px ${PADDING_X_MEDIUM}px;
        text-align: left;
    }

    ${breakpoint.large} {
        font-size: 28px;
        padding: 23px ${PADDING_X_LARGE}px;
    }
`;


const InnerBookingWrapper = styled.div`
    padding-bottom: 8px;

    ${breakpoint.medium} {
        padding-bottom: 20px;
        padding-left: ${PADDING_X_MEDIUM}px;
        padding-right: ${PADDING_X_MEDIUM}px;
    }

    ${breakpoint.large} {
        padding-left: ${PADDING_X_LARGE}px;
        padding-right: ${PADDING_X_LARGE}px;
    }
`;

const NotAvailableTextWrapper = styled(InnerBookingWrapper)`
    margin-top: 20px;
`;

const CloseButton = styled(Close)`
    cursor: pointer;
    font-size: 18px;
    padding: 3px;
    position: absolute;
    right: ${PADDING_X_XXSMALL}px;
    top: 9px;
`;

const UnderlineHeader = styled.hr`
    background-color: ${p => p.theme.profile.textColor};
    border-color: ${p => p.theme.profile.textColor};
    color: ${p => p.theme.profile.textColor};
    display: none;
    height: 4px;
    margin-bottom: 12px;
    margin-top: 18px;
    ${breakpoint.medium} {
        display: block;
    }
`;

const NotAvailableText = styled.div`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    padding: 12px 30px;
    text-align: center;

    ${breakpoint.small} {
        padding: 16px 60px;
    }

    ${breakpoint.medium} {
        padding: 0;
        font: 18px/28px ${FontFamily.SharpSansSemibold};
        text-align: left;
    }
`;

const NotAvailableCloseButton = styled(Button)`
    width: 100%;
    margin-top: 28px;
    display: block;

    ${breakpoint.medium} {
        display: none;
    }
`;


const SeeAllLocationsToggleWrapper = styled.div`
    margin-top: 30px;
    text-align: center;
`;

const SeeAllLocationsToggle = styled.span`
    cursor: pointer;
    padding: 8px;
`;

const SeeAllLocationsToggleIcon = styled(Arrow)`
    font-size: 9px;
    margin-right: 12px;
`;
